import { ComposableElement } from '../../ComposableElement.mjs';

/** 
 * Tab Panels Web Component
 * 
 * This component is responsible for rendering the content panels of a given tabbed interface. 
 * It's designed to work in conjunction with more interaction heavy components such as the `ba-tablist` component.
 * Or another way, it's focused on display / view rather than interactivity (though it does have some internal logic to manage the active tab panel).
 */
export class TabPanels extends ComposableElement {
    constructor() {
        super();
    }

    /**
     * Does initial tab setup and adds event listeners for tab interaction.
     * 
     * `connectedCallback` is a lifecycle method in web components that runs when the custom element is inserted into the document's Document Object Model (DOM). 
     * It can be invoked multiple times if the element is removed and then re-inserted into the DOM.
     * 
     * Timing: It is called after the element's constructor() but before the element's children are necessarily connected or fully rendered.
     * Purpose: It is the ideal place to set up tasks that should only occur when the element is actually present in the live document. Common uses include:
     */
    connectedCallback() {
        // Attach the internals to the component to allow for the use of a Declarative Shadow Root
        const internals = this.attachInternals();
        
        // check for a Declarative Shadow Root:
        this.shadow = this.shadowRoot;
        if (!this.shadow) {
            // there wasn't one. create a new Shadow Root:
            this.shadow = this.attachShadow({ mode: 'open' });

            setTimeout(() => {
                // Recreate the template using the shadow DOM that is only available through JavaScript
                this.createTemplateInJS(this.shadow);
            }, 0);
        }

        setTimeout(() => {
            const activePanel = this.shadow.querySelector('[role="tabpanel"][aria-hidden="false"]') || this.shadow.querySelector('[role="tabpanel"]');

            if(activePanel == null) {
                throw new Error('No tab panels found. Please ensure that your configuration includes at least one tab panel.');
            }

            this.activeTabPanelId = activePanel.id;

            // Ensure it's visually active if we fell back to the first one
            if (!activePanel.classList.contains('is-active')) {
                this.switchPanel(this.activeTabPanelId);
            }
        }, 0);
    }

    /**
     * Get the currently active tab.
     * 
     * useful for external interactions with the component.
     * Note, there is some ambiguity in what the "active tab" means.
     * 
     * For that reason we return an object that can be summarized using the following table:
     * 
     * | Key     | Description               | Rational                                                                                                       |
     * | ------- | ------------------------- | -------------------------------------------------------------------------------------------------------------- |
     * | `id`    | The ID of the the panel   | If the question is "what is the ID of the active tab?" Your not looking for the ID of the button element       |
     * | `panel` | The panel (tab contents)  | Provides access to the underlying panel (tab contents) if/when needed while not over-complicating other things |
     * 
     * @return {{ id: string, panel: HTMLDivElement }} The currently active tab element
     */
    getActiveTab() {
        const panel = this.shadow.getElementById(this.activeTabPanelId);
        
        return { id: this.activeTabPanelId, panel };
    }

    /**
     * Switch the active tab panel to the one with the specified ID.
     * 
     * Note, this is meant to be available both internally to the class but also externally (if JavaScript ever needed to programmatically switch the active tab, they could call this method directly) so it is not prefixed with an underscore like the other internal helper methods.
     * 
     * @param {string} targetId The ID of the tab panel to switch to (e.g. "tab-panel-1")
     */
    switchPanel(targetId) {
        // Loop through each tab panel and show the one that corresponds to the selected tab and hide the others
        this.shadow.querySelectorAll('[role="tabpanel"]').forEach(panel => {
            const isTarget = panel.id === targetId;

            // If it's not the selected tab and it doesn't have the hidden attribute, add it (so we hide the tab)
            if(!isTarget && (!panel.hasAttribute('hidden') || panel.getAttribute('aria-hidden') === 'false')) {
                panel.setAttribute('hidden', 'true');
                panel.setAttribute('aria-hidden', 'true');
                panel.classList.remove('is-active');
            }

            // If it's the selected tab and it has the hidden attribute, remove it (so we show the tab)
            if(isTarget && (panel.hasAttribute('hidden') || panel.getAttribute('aria-hidden') === 'true')) {
                // Because of weird edge cases we do an extra check here
                if(panel.hasAttribute('hidden')) {
                    panel.removeAttribute('hidden');
                }

                panel.setAttribute('aria-hidden', 'false');
                panel.classList.add('is-active');
            }
        });

        this.activeTabPanelId = targetId;
    }

    /**
     * Recreate the template in the shadow DOM through JavaScript instead of relying on the `shadowrootmode` attribute
     * 
     * @param {ShadowRoot} shadow The shadow DOM to attach the template to
     */
    createTemplateInJS(shadow) {
        const config = this.initializeComponent('tab-panels', shadow, { stylesAndScriptsNameOverride: 'tabs' });
        const { tabsId, tabsMeta } = config;

        // Create the div that will contain the tab panels
        const tabsContentDiv = document.createElement('div');
        tabsContentDiv.classList.add('tabs-content');
        tabsContentDiv.dataset.tabsContent = tabsId;

        // Generate Tab Panels (div elements)
        tabsMeta.forEach((tab, index) => {
            // Create the first tab panel element
            const tabPanelDiv = document.createElement('div');
            tabPanelDiv.role = 'tabpanel';
            tabPanelDiv.id = tab.id; 
            tabPanelDiv.setAttribute('aria-labelledby', `tab-${index}`);
            tabPanelDiv.classList.add('tabs-panel');

            if(tab.active) {
                tabPanelDiv.setAttribute('aria-hidden', 'false');
                tabPanelDiv.classList.add('is-active');
            }
            else {
                tabPanelDiv.setAttribute('hidden', 'true');
                tabPanelDiv.setAttribute('aria-hidden', 'true');
            }
        
            // Create a slot element for the tab panel's content
            const tabPanelSlotElem = document.createElement('slot'); 
            tabPanelSlotElem.name = `tab-content-${index}`;
        
            // Append the slot element to the tab panel element
            tabPanelDiv.appendChild(tabPanelSlotElem);

            // Append the tab panel element to the tabs content div
            tabsContentDiv.appendChild(tabPanelDiv);
        });

        // Finally, append the entire tabs content div to the shadow DOM of the component
        shadow.appendChild(tabsContentDiv);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    if (!customElements.get('ba-tab-panels')) {
        customElements.define('ba-tab-panels', TabPanels);
    }
});